import { UI, HStack, VStack, Stack } from '@genial/components';
import { BoxRadius, Typography, BoxBorder } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { ResponsiveSuccessIconButton } from '../../../../../../../../../components/ResponsiveSuccessIconButton';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import {
  TableContentItemCaptions,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useFixedIncomeAssetController } from '../../../../../hooks/products/FixedIncome';
import { LateralBar } from '../../../components/LateralBar';

import { FixedIncomeItemSelectedTypes } from '.';

export const FixedIncomeItemSelectedMobile: React.FC<FixedIncomeItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
  hideLateralBar = false,
}) => {
  const id = offer.productIdNormalized;
  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    helpTextProps,
    isFocused,
    showMessageAdjustUnitPrice,
    currentValueFormatted,
    currentValue,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  } = useFixedIncomeAssetController(offer);

  useEffect(() => {
    if (offer.orderValue === currentValue || !currentValue) {
      return;
    }
    const updatedOffer = {
      ...offer,
      orderValue: currentValue || 0,
      estimatedIncomeCalculated,
      estimatedNetValueCalculated,
    };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (offer.orderFee?.feeId === selectedFee?.feeId || !selectedFee) {
      return;
    }
    const updatedOffer = {
      ...offer,
      orderFee: selectedFee,
      estimatedIncomeCalculated,
      estimatedNetValueCalculated,
    };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFee]);

  const middleInfos = [
    {
      label: 'Risco',
      value: (
        <RiskRating
          currentRiskRating={Number(offer.riskRatingLevelNormalized) || 0}
          tooltip={offer.riskRatingName || ''}
        />
      ),
    },
    {
      label: 'IR',
      value: <UI.Text>{offer.incomeTaxAmountNormalized}</UI.Text>,
    },
    {
      label: 'Rentabilidade anual',
      value: (
        <UI.Text>{selectedFee?.profitability || offer.profitabilityFormattedNormalized}</UI.Text>
      ),
    },
    {
      label: 'Vencimento',
      value: <UI.Text>{offer.maturityDateNormalized}</UI.Text>,
    },
    {
      label: 'Carência',
      value: <UI.Text>{offer.dueDateNormalized}</UI.Text>,
    },
    {
      label: 'Preço unitário',
      value: <UI.Text>{selectedFee?.customerPuAmountFormatted || '--'}</UI.Text>,
    },
    {
      label: 'Fee',
      value: <UI.Text>{selectedFee?.honorariumFee}</UI.Text>,
    },
    {
      label: 'Receita estimada',
      value: <UI.Text>{Utils.Mask.Money(estimatedIncomeCalculated)}</UI.Text>,
    },
    {
      label: 'Líquido estimado',
      value: <UI.Text>{Utils.Mask.Money(estimatedNetValueCalculated)}</UI.Text>,
    },
  ];

  return (
    <VStack space="stack.xxxs" w="full">
      {!hideLateralBar && (
        <LateralBar
          backgroundColor="product.rf"
          borderTopLeftRadius={BoxRadius.sm}
          borderBottomLeftRadius={BoxRadius.sm}
        />
      )}
      <VStack space="stack.nano">
        <UI.Text variant="semibold" color="neutral.black">
          {offer.productDisplayNormalized}
        </UI.Text>
        <TableContentItemCaptions icons={offer.captions} forceDesktop />
      </VStack>
      <VStack space="stack.nano" flexGrow={1}>
        {middleInfos.map(({ label, value }) => (
          <HStack space="inline.quarck" justifyContent="space-between" key={label}>
            <UI.Text color="neutral.dark">{label}</UI.Text>
            {value}
          </HStack>
        ))}
      </VStack>
      <VStack space="stack.quarck">
        <HStack space="inline.quarck" alignItems="center">
          <UI.Text>Taxa</UI.Text>
          <UI.Text fontSize="xs">(Spread)</UI.Text>
        </HStack>
        <Stack bgColor="backgroundshade" borderRadius={BoxBorder.thick}>
          <UI.Select
            triggerProps={{
              isDisabled: !ableToChangeFees,
              _disabled: { opacity: 0.2 },
            }}
            testID="fee-select"
            options={offer.distributionFeesNormalized.map((feeOption) => ({
              label: feeOption.label,
              value: feeOption.feeId.toString(),
            }))}
            placeholder="--"
            selectedValue={selectedFeeId?.toString() || ''}
            onValueChange={(value) => setSelectedFeeId(Number(value))}
          />
        </Stack>
      </VStack>
      <HStack space="inline.xxxs" justifyContent="space-between" w="full" alignItems="flex-end">
        <VStack space="stack.quarck" flexGrow={1}>
          <UI.Text color="neutral.black">Valor a aplicar</UI.Text>
          <VStack flex={1}>
            <InputMoney
              id={`fixedIncome-input${id}`}
              name="createAppliedAmount"
              control={control}
              handleFocus={handleInputFocus}
              handleBlur={handleInputBlur}
              handleSubmit={handleSubmit}
              handleClear={handleInputClear}
              currentValue={currentValueFormatted}
              hasFocus={isFocused}
              minValue={minApplication}
              customInputProps={{
                ...{
                  ...(showMessageAdjustUnitPrice && {
                    helpTextProps,
                    helpText,
                  }),
                },
                ...{ ...(isFocused && hasError && { error: hasError }) },
                ...{ ...(isFocused && !hasError && { helpText }) },
              }}
            />
            {showMessageAdjustUnitPrice && !isFocused && (
              <TableContentItemText fontSize={Typography['fontsize.xs']} color="neutral.light">
                Ajustado por preço unitário
              </TableContentItemText>
            )}
          </VStack>
        </VStack>
        <ResponsiveSuccessIconButton />
      </HStack>
    </VStack>
  );
};
